import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardGrid, DrilldownModal } from '../../components/Dashboard';
import { getUserInfoDetails } from '../../utlis';
import { getUserPreferences, updateConfig, createConfig } from '../../api/administration';
import { toast } from 'react-toastify';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Build config type key from username
const buildPrefType = (userName) => {
  if (!userName) return null;
  return `${userName}_user_preferences`;
};

const Dashboard = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [gridWidth, setGridWidth] = useState(1200);
  const [savedLayout, setSavedLayout] = useState(null);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef(null);
  const configIdRef = useRef(null);
  const prefTypeRef = useRef(null);
  const currentLayoutRef = useRef(null);

  // Handle responsive width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setGridWidth(containerRef.current.offsetWidth - 32);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Fetch user preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const userInfo = getUserInfoDetails();
        const userName = userInfo?.user_name;
        const prefType = buildPrefType(userName);
        if (!prefType) {
          setLayoutLoaded(true);
          return;
        }
        prefTypeRef.current = prefType;

        const res = await getUserPreferences(prefType);
        const record = res.data?.data || res.data || {};
        const configId = record.id || record.configId;
        const configValue = record.value || {};

        if (configId) {
          configIdRef.current = configId;
        }

        if (configValue.dashboardLayout && Array.isArray(configValue.dashboardLayout)) {
          setSavedLayout(configValue.dashboardLayout);
        }
      } catch (err) {
        // No saved preferences yet — will use default layout
        console.log('No saved dashboard preferences found, using defaults');
      } finally {
        setLayoutLoaded(true);
      }
    };

    fetchPreferences();
  }, []);

  // Track layout changes — mark dirty when user drags/resizes
  const handleLayoutChange = useCallback((newLayout) => {
    currentLayoutRef.current = newLayout;
    setLayoutDirty(true);
  }, []);

  // Save layout to config API on button click
  const handleSaveLayout = async () => {
    const newLayout = currentLayoutRef.current;
    if (!newLayout) return;

    const prefType = prefTypeRef.current;
    if (!prefType) {
      toast.error('User session not found. Please log in again.');
      return;
    }

    setIsSaving(true);
    try {
      const valuePayload = { dashboardLayout: newLayout };

      if (configIdRef.current) {
        // Update existing config record
        await updateConfig(configIdRef.current, { value: valuePayload });
      } else {
        // Create new config record
        const res = await createConfig({
          type: prefType,
          value: valuePayload,
        });
        const newId = res.data?.data?.configId || res.data?.configId || res.data?.data?.id || res.data?.id;
        if (newId) {
          configIdRef.current = newId;
        }
      }

      localStorage.setItem('dashboardLayout', JSON.stringify(newLayout));
      setLayoutDirty(false);
      toast.success('Layout saved successfully');
    } catch (err) {
      console.error('Failed to save dashboard layout:', err);
      const errorMessage = err.response?.data?.message || err.message || '';
      toast.error(errorMessage || 'Failed to save layout');
    } finally {
      setIsSaving(false);
    }
  };

  const handleWidgetClick = (data) => {
    setModalData(data);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalData(null);
  };

  return (
    <div ref={containerRef} className="p-4 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Drag and resize widgets to customize your view
          </p>
        </div>
        {layoutDirty && (
          <button
            onClick={handleSaveLayout}
            disabled={isSaving}
            className={`py-2 px-6 rounded-full shadow transition font-medium ${
              isSaving
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-secondary-700 text-white hover:bg-secondary-800 cursor-pointer'
            }`}
          >
            {isSaving ? 'Saving...' : 'Save Layout'}
          </button>
        )}
      </div>

      {/* Dashboard Grid */}
      {layoutLoaded && (
        <DashboardGrid
          onWidgetClick={handleWidgetClick}
          width={gridWidth}
          savedLayout={savedLayout}
          onLayoutSave={handleLayoutChange}
        />
      )}

      {/* Drilldown Modal */}
      <DrilldownModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        data={modalData}
      />
    </div>
  );
};

export default Dashboard;
